import { useEffect, useRef, useState } from 'react';

// T082 — Status pill with a hover/focus legend popover. Operators
// (especially new ones — first-login from the wizard) often don't know
// what the pipeline stages mean. Hovering the pill now shows a small
// legend explaining the current stage + how to advance it.
//
// All statuses surface from one shared source of truth (STAGES below) so
// the dashboard + Kanban + side drawer can't drift on copy / colour.

export const STAGES = {
  new: {
    label: 'New',
    tone: 'border-slate-700 text-slate-300 bg-slate-900/50',
    desc: 'A fresh recommendation the scanner surfaced. Triage it: shortlist, dismiss, or apply.',
    next: 'Move to Shortlisted, Applied, or Dismissed.',
  },
  shortlisted: {
    label: 'Shortlisted',
    tone: 'border-brand-700 text-brand-500 bg-brand-600/15',
    desc: 'Worth a closer look — research the company, draft the cover letter, then apply.',
    next: 'Move to Applied once you hit Send.',
  },
  dismissed: {
    label: 'Dismissed',
    tone: 'border-slate-700 text-slate-500 bg-slate-900/50',
    desc: 'Not a fit. Hidden from the main list but recoverable from the Dismissed panel.',
    next: 'Click "Un-dismiss" to bring it back to New.',
  },
  applied: {
    label: 'Applied',
    tone: 'border-slate-700 text-slate-300 bg-slate-900/50',
    desc: 'You hit Send. The CV version + date are saved so callbacks weeks later still match.',
    next: 'Move to Screening when the recruiter reaches out.',
  },
  screening: {
    label: 'Screening',
    tone: 'border-brand-700 text-brand-500 bg-brand-600/15',
    desc: 'Recruiter / phone screen stage. Often the gate before a technical loop.',
    next: 'Move to Interview when the loop is scheduled.',
  },
  interview: {
    label: 'Interview',
    tone: 'border-amber-700 text-amber-300 bg-amber-950/30',
    desc: 'In an active interview loop. Pull up Interview Prep + Company Research from the side drawer.',
    next: 'Move to Offer if the call comes — or Rejected / Withdrawn if it ends.',
  },
  offer: {
    label: 'Offer',
    tone: 'border-emerald-700 text-emerald-300 bg-emerald-950/30',
    desc: 'You got the offer. Negotiate or accept.',
    next: 'Set the outcome (Accepted / Declined / Expired) from the side drawer once you decide.',
  },
  rejected: {
    label: 'Rejected',
    tone: 'border-red-800 text-red-400 bg-red-950/30',
    desc: 'They passed. Logged for the retrospective.',
    next: 'Terminal — keep the row to learn from in the application history.',
  },
  withdrawn: {
    label: 'Withdrawn',
    tone: 'border-slate-700 text-slate-500 bg-slate-900/50',
    desc: 'You pulled out (took another role, lost interest, etc).',
    next: 'Terminal — keep the row for the retrospective.',
  },
};

export default function StatusPill({ status, className = '', compact = false }) {
  const meta = STAGES[status] ?? STAGES.new;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Outside-click closes the popover (matters on touch where there's no
  // mouseleave — the operator taps the pill to open then taps elsewhere).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // T115 — compact inline pill (no popover) for dense rows like the Available
  // table. Hooks above stay unconditional; this conditional return is legal
  // because it sits after all of them.
  if (compact) {
    return (
      <span
        title={meta.label}
        className={'inline-block rounded-full border px-1.5 py-0 text-[10px] leading-4 ' + meta.tone + ' ' + className}
      >
        {meta.label}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={
          'cursor-help rounded-full border px-2.5 py-0.5 text-xs ' +
          meta.tone + ' ' + className
        }
        aria-label={`Stage: ${meta.label} — click for details`}
      >
        {meta.label}
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-full z-30 mt-2 w-72 rounded-lg border border-slate-700 bg-slate-900/95 p-3 text-left text-xs text-slate-200 shadow-xl backdrop-blur"
        >
          <p className="font-semibold text-slate-100">{meta.label}</p>
          <p className="mt-1 leading-snug text-slate-300">{meta.desc}</p>
          <p className="mt-2 text-[10px] uppercase tracking-widest text-slate-500">Next move</p>
          <p className="mt-0.5 leading-snug text-slate-400">{meta.next}</p>
        </div>
      )}
    </span>
  );
}
